import {
  takeLatest,
  takeLeading,
  call,
  put,
  select,
  takeEvery,
  spawn,
} from 'redux-saga/effects';

import { push } from 'connected-react-router';

// Services
import { usersApi } from 'services/attendance/http1/users';
import { messagesApi } from 'services/attendance/http1/messages';
import { tagsApi } from 'services/attendance/http1/tags';
import { userTagsApi } from 'services/attendance/http1/userTags';
import { messageTagsApi } from 'services/attendance/http1/messageTags';
import { qualificationApi } from 'services/attendance/http1/qualification';
import { notificationApi } from 'services/attendance/http1/notification';
import { replyApi } from 'services/attendance/http1/reply';
import { fileApi } from 'services/attendance/http1/file';
import { tokenApi } from 'services/attendance/http1/token';
import { officeHoursApi } from 'services/attendance/http2/officeHous';
import { attendantsApi } from 'services/attendance/http2/attendants';
import { heatMapChartApi } from 'services/attendance/http2/heatMapChart';
import { wordCountChartApi } from 'services/attendance/http2/wordCountChart';
import { tagsLinesChartApi } from 'services/attendance/http2/tagsLinesChart';
import { ratingApi } from 'services/attendance/http2/rating';
import { closeConversationChartApi } from 'services/attendance/http2/closeConversation';
import { firstReplyChartApi } from 'services/attendance/http2/firstReplyChart';
import { conversationReplyChartApi } from 'services/attendance/http2/conversationReplyChart';
import { messagesCountApi } from 'services/attendance/http2/messagesCount';
import { webhookApi } from 'services/attendance/http2/webhook';
import { filterUsersApi } from 'services/attendance/http2/filterUsers';
import { attendancesApi } from 'services/attendance/http2/attendanceHistory';
import { auth } from 'services/firebase';
import { sendPasswordResetEmail } from 'firebase/auth';
import { numberOfMessagesApi } from 'services/attendance/http2/numberOfMessages';

// Helpers
import {
  getCompany,
  setAttendantUsername,
  getId,
  getMessagingToken,
} from 'helpers/auth';
import { getSenderDisplayName } from 'helpers/attendance';

// Constants
import ENVS from 'constants/enviroments';
import CONST_ATTENDANCE from 'constants/attendance';
import { errorMessagersList } from 'helpers/errorMessagers';
import PATH from 'constants/basePath';

// Socket
import socket from 'services/attendance/websocket';

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';

// Application State
import { ApplicationState } from 'store';
import * as AttendanceActions from './actions';

import {
  AttendanceTypes,
  AttendantInterface,
  TagsInterface,
  UserInterface,
} from './types';

// v2 backend: message items carry a string ID ('<epoch ms>#<random>') and no
// numeric id. Mirror ID into the local `id` field the UI keys on.
const normalizeMessage = (message: any) =>
  message && message.ID !== undefined && message.id === undefined
    ? { ...message, id: message.ID }
    : message;

// Only when there is no assignment

function* updateAttendantOwner(data: any) {
  const { Room, attendantId } = data;

  const Attendants: AttendantInterface[] = yield select(
    (state: ApplicationState) => state.attendance.Attendants,
  );

  const openedUsers: UserInterface[] = yield select(
    (state: ApplicationState) => state.attendance.openedUsers,
  );

  const attendant = Attendants.find(
    (localAttendant: AttendantInterface) => localAttendant.ID === attendantId,
  );

  const user = openedUsers.find((user: UserInterface) => user.Room === Room);

  if (!user!.attendantOwnerID) {
    yield put(
      AttendanceActions.setAttendantAssignmentSuccess({
        data: {
          room: Room,
          attendantId,
          attendantUsername: attendant!.name,
          previousAttendantId: '',
          previousAttendantUsername: '',
          isNewAttendant: true,
        },
      }),
    );
  }
}

// Listen events from ws
function* listenMessage(data: any) {
  const frame = JSON.parse(data);

  const { messages, eventType, room, typingStatus, user, attendantId, Room } =
    frame;

  const messageData = normalizeMessage(frame.messageData);

  let users;
  let selectedUser;

  switch (eventType) {
    case CONST_ATTENDANCE.EVENT_TYPE.USER_NEW_MESSAGE:
      // Add message
      yield put(
        AttendanceActions.loadNewMessageSuccess({
          message: { ...frame, messageData },
        }),
      );
      // Send message received to user — messageId is the v2 string ID
      socket.send(
        JSON.stringify({
          action: CONST_ATTENDANCE.EVENT_TYPE.MESSAGE_STATUS,
          eventType:
            CONST_ATTENDANCE.EVENT_TYPE.ATTENDANT_RECEIVED_MESSAGE_STATUS,
          CompanyID: getCompany(),
          room: Room,
          messageId: messageData.id,
        }),
      );

      // Send notification
      yield call(notificationApi.create, {
        data: {
          title: messageData.username,
          body: messageData.message,
          tokens: [getMessagingToken()],
        },
      });

      // Play notification
      new Audio('/notification.mp3').play();

      break;

    case CONST_ATTENDANCE.EVENT_TYPE.TYPING_STATUS:
      yield put(
        AttendanceActions.setTypingStatusSuccess({
          typingStatus,
          Room: room,
        }),
      );

      break;
    case CONST_ATTENDANCE.EVENT_TYPE.GET_NEW_USER:
      yield put(
        AttendanceActions.loadNewUserSuccess({
          user,
        }),
      );

      break;

    case CONST_ATTENDANCE.EVENT_TYPE.CONVERSATION_STATUS:
      // @ts-ignore
      users = yield select(
        (state: ApplicationState) => state.attendance.openedUsers,
      );

      selectedUser = users.find((user: any) => user.Room === Room);

      yield put(
        AttendanceActions.setClosedDialogueSuccess({
          status: 0,
          user: selectedUser,
        }),
      );
      break;

    case CONST_ATTENDANCE.EVENT_TYPE.USER_VISUALIZED_STATUS:
      yield put(
        AttendanceActions.setVisualizedMessageStatusSuccess({
          Room,
          messageStatus: messages,
        }),
      );

      break;

    case CONST_ATTENDANCE.EVENT_TYPE.ATTENDANT_NEW_MESSAGE:
      if (attendantId === getId()) {
        yield put(
          AttendanceActions.setAttendantMessageStatusSuccess({
            Room,
            messageData,
          }),
        );
      } else {
        yield put(
          AttendanceActions.loadNewMessageSuccess({
            message: {
              Room,
              messageData,
            },
          }),
        );

        yield call(updateAttendantOwner, { Room, attendantId });
      }
      break;
    case CONST_ATTENDANCE.EVENT_TYPE.ATTENDANT_ASSIGNMENT:
      if (attendantId !== getId()) {
        yield put(
          AttendanceActions.loadNewMessageSuccess({
            message: {
              Room,
              messageData,
            },
          }),
        );

        yield call(updateAttendantOwner, { Room, attendantId });
      }
      break;
    default:
  }
}

// Websocket messages
export function* handleWebsocketConnection({
  payload: { data },
}: ReturnType<typeof AttendanceActions.initWebsocketChannel>) {
  yield spawn(listenMessage, data);
}

// Messages
function* getMessages({
  payload: { Room, limit },
}: ReturnType<typeof AttendanceActions.getMessagesRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando' }));
    const response = yield call(messagesApi.get, { Room, limit });

    if (response.status === 200) {
      const data = response.data || {};

      yield put(
        AttendanceActions.getMessagesSuccess({
          MessageData: {
            ...data,
            messages: (data.messages || []).map(normalizeMessage),
            nextCursor: data.nextCursor || null,
          },
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (err) {
    yield put(DialogsActions.setClose());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível listar as mensagens',
      }),
    );
    yield put(AttendanceActions.getMessagesFailure());
  }
}

// Older messages page (v2 pagination): prepends to the loaded history
function* getOlderMessages({
  payload: { Room, cursor },
}: ReturnType<typeof AttendanceActions.getOlderMessagesRequest>): any {
  // The cursor must be the stored nextCursor verbatim: null means the start
  // of the history was reached, and a request without a cursor would fetch
  // the newest page again and duplicate it into the conversation
  const nextCursor = yield select(
    (state: ApplicationState) => state.attendance.messageData.nextCursor,
  );

  if (!cursor || cursor !== nextCursor) {
    // The REQUEST reducer already set loading — reset it (no snackbar)
    yield put(AttendanceActions.getOlderMessagesFailure());
    return;
  }

  try {
    const response = yield call(messagesApi.get, { Room, cursor });

    if (response.status === 200) {
      const data = response.data || {};
      const messages = (data.messages || []).map(normalizeMessage);

      yield put(
        AttendanceActions.getOlderMessagesSuccess({
          Room,
          messages,
          // Empty page = reached the oldest message
          nextCursor: messages.length ? data.nextCursor || null : null,
        }),
      );
    }
  } catch (err) {
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível carregar mensagens anteriores',
      }),
    );
    yield put(AttendanceActions.getOlderMessagesFailure());
  }
}

// Opened Users
function* listOpenedUsers({
  payload: { Room, Limit, UpdatedAt, ConversationStatus, silent },
}: ReturnType<typeof AttendanceActions.listOpenedUsersRequest>): any {
  try {
    if (!silent) {
      yield put(DialogsActions.setOpenLoading({ title: 'Carregando' }));
    }

    const response = yield call(usersApi.list, {
      Room,
      Limit,
      UpdatedAt,
      ConversationStatus,
    });

    const firstRequest = !Room;

    const Users = response.data.map((user: any) => ({
      ...user,
      selectedUser: false,
    }));

    if (response.status === 200) {
      if (response.data.length) {
        yield put(
          AttendanceActions.listOpenedUsersSuccess({
            Users,
            firstRequest,
          }),
        );
      } else {
        yield put(AttendanceActions.loadHasMoreOpenedUsersSuccess());
      }

      if (!silent) {
        yield put(DialogsActions.setClose());
      }
    }
  } catch (err) {
    if (!silent) {
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'error',
          message: 'Não foi possível listar os usuários abertos',
        }),
      );
    }

    yield put(AttendanceActions.listOpenedUsersFailure());
  }
}

// Chatbot Users
function* listChatbotUsers({
  payload: { Room, Limit, UpdatedAt, ConversationStatus },
}: ReturnType<typeof AttendanceActions.listChatbotUsersRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando' }));
    const response = yield call(usersApi.list, {
      Room,
      Limit,
      UpdatedAt,
      ConversationStatus,
    });

    const firstRequest = !Room;

    const Users = response.data.map((user: any) => ({
      ...user,
      selectedUser: false,
    }));

    if (response.status === 200) {
      if (response.data.length) {
        yield put(
          AttendanceActions.listChatbotUsersSuccess({
            Users,
            firstRequest,
          }),
        );
      } else {
        yield put(AttendanceActions.loadHasMoreChatbotUsersSuccess());
      }

      yield put(DialogsActions.setClose());
    }
  } catch (err) {
    yield put(DialogsActions.setClose());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível listar os usuários de chatbot',
      }),
    );
    yield put(AttendanceActions.listChatbotUsersFailure());
  }
}

// Closed Users
function* listClosedUsers({
  payload: { Room, Limit, UpdatedAt, ConversationStatus },
}: ReturnType<typeof AttendanceActions.listClosedUsersRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando' }));
    const response = yield call(usersApi.list, {
      Room,
      Limit,
      UpdatedAt,
      ConversationStatus,
    });

    const firstRequest = !Room;

    const Users = response.data.map((user: any) => ({
      ...user,
      selectedUser: false,
    }));

    if (response.status === 200) {
      if (response.data.length) {
        yield put(
          AttendanceActions.listClosedUsersSuccess({
            Users,
            firstRequest,
          }),
        );
      } else {
        yield put(AttendanceActions.loadHasMoreClosedUsersSuccess());
      }

      yield put(DialogsActions.setClose());
    }
  } catch (err) {
    yield put(DialogsActions.setClose());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível listar os usuários fechados',
      }),
    );
    yield put(AttendanceActions.listOpenedUsersFailure());
  }
}

// Filter users by name
function* filterUsersByName({
  payload: { username },
}: ReturnType<typeof AttendanceActions.filterUsersByNameRequest>): any {
  try {
    const response = yield call(filterUsersApi.filterUserByName, {
      username,
    });

    if (response.status === 200) {
      yield put(
        AttendanceActions.filterUsersByNameSuccess({
          Users: response.data,
        }),
      );
    }
  } catch (err) {
    yield put(DialogsActions.setClose());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível filtrar os usuários por nome',
      }),
    );
    yield put(AttendanceActions.filterUsersByNameFailure());
  }
}

// Filter users by message
function* filterUsersByMessage({
  payload: { message },
}: ReturnType<typeof AttendanceActions.filterUsersByMessageRequest>): any {
  try {
    const response = yield call(filterUsersApi.filterUserByMessage, {
      message,
    });

    if (response.status === 200) {
      yield put(
        AttendanceActions.filterUsersByMessageSuccess({
          Users: response.data,
        }),
      );
    }
  } catch (err) {
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível filtrar os usuários por mensagem',
      }),
    );
    yield put(AttendanceActions.filterUsersByMessageFailure());
  }
}

// Filter users by tag
function* filterUsersByTag({
  payload: { tagId },
}: ReturnType<typeof AttendanceActions.filterUsersByTagRequest>): any {
  try {
    const response = yield call(filterUsersApi.filterUserByTag, {
      tagId,
    });

    if (response.status === 200) {
      yield put(
        AttendanceActions.filterUsersByTagSuccess({
          Users: response.data,
        }),
      );
    }
  } catch (err) {
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível filtrar os usuários por tag',
      }),
    );
    yield put(AttendanceActions.filterUsersByTagFailure());
  }
}

// Filter users by tag
function* filterTagByName({
  payload: { tag },
}: ReturnType<typeof AttendanceActions.filterTagByNameRequest>): any {
  try {
    const response = yield call(filterUsersApi.filterTagByName, {
      tag,
    });

    if (response.status === 200) {
      yield put(
        AttendanceActions.filterTagByNameSuccess({
          tags: response.data,
        }),
      );
    }
  } catch (err) {
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível filtrar as tags',
      }),
    );
    yield put(AttendanceActions.filterTagByNameFailure());
  }
}

// Update user
function* updateUser({
  payload: { Room, attendantId, username, conversationStatus },
}: ReturnType<typeof AttendanceActions.updateUserRequest>): any {
  try {
    const response = yield call(usersApi.update, {
      Room,
      attendantId,
      username,
      conversationStatus,
    });

    if (response.status === 200) {
      yield put(
        AttendanceActions.updateUserSuccess({
          Room,
          attendantId,
          username,
        }),
      );
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Usuário atualizado com sucesso',
        }),
      );
    }
  } catch (err) {
    yield put(DialogsActions.setClose());
    yield put(AttendanceActions.updateUserFailure());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível atualizar o usuário',
      }),
    );
  }
}

// Tags
function* getTag({
  payload: { ID },
}: ReturnType<typeof AttendanceActions.getTagRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando' }));
    const response = yield call(tagsApi.get, { ID });

    if (response.status === 200) {
      yield put(AttendanceActions.getTagSuccess({ Tag: response.data }));
      yield put(DialogsActions.setClose());
    }
  } catch (err) {
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível editar a tag',
      }),
    );
    yield put(AttendanceActions.getTagFailure());
  }
}

function* listTags({
  payload,
}: ReturnType<typeof AttendanceActions.listTagsRequest>): any {
  try {
    const { body, filtering } = payload;
    if (!filtering) {
      yield put(DialogsActions.setOpenLoading({ title: 'Carregando' }));
    }
    const response = yield call(tagsApi.list, { params: { ...body } });

    if (response.status === 200) {
      yield put(
        AttendanceActions.listTagsSuccess({
          Tags: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (err) {
    yield put(DialogsActions.setClose());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível exibir as tags',
      }),
    );
    yield put(AttendanceActions.listTagsFailure());
  }
}

function* filterTags({
  payload,
}: ReturnType<typeof AttendanceActions.filterTagsRequest>): any {
  try {
    const response = yield call(tagsApi.list, { params: { ...payload } });

    if (response.status === 200) {
      yield put(
        AttendanceActions.filterTagsSuccess({
          Tags: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (err) {
    yield put(DialogsActions.setClose());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível exibir as tags',
      }),
    );
    yield put(AttendanceActions.filterTagsFailure());
  }
}

function* createTag({
  payload: { Tag },
}: ReturnType<typeof AttendanceActions.createTagsRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Criando Tag' }));
    const response = yield call(tagsApi.create, { data: { tag: Tag } });

    if (response.status === 200) {
      const { ID } = response.data;

      yield put(
        AttendanceActions.createTagsSuccess({
          Tag: {
            ID,
            tag: Tag,
            userRegister: [],
            messageRegister: [],
          },
        }),
      );

      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Tag criada com sucesso',
        }),
      );
      yield put(push(PATH.ATTENDANCE.CONFIGURATIONS));
    }
  } catch (err) {
    yield put(DialogsActions.setClose());
    yield put(AttendanceActions.createUserTagsFailure());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível criar a tag',
      }),
    );
  }
}

function* updateTag({
  payload: { ID, Tag, createdAt },
}: ReturnType<typeof AttendanceActions.updateTagsRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando' }));
    const response = yield call(tagsApi.update, {
      data: { tag: Tag, createdAt },
      ID,
    });

    if (response.status === 200) {
      yield put(AttendanceActions.updateTagsSuccess({ Tag, ID }));
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Tag atualizada com sucesso',
        }),
      );
    }
  } catch (err) {
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível editar a tag',
      }),
    );
    yield put(AttendanceActions.updateTagsFailure());
  }
}

function* deleteTag({
  payload: { ID },
}: ReturnType<typeof AttendanceActions.deleteTagsRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Deletando Tag' }));
    const response = yield call(tagsApi.delete, { ID });

    if (response.status === 200) {
      yield put(
        AttendanceActions.deleteTagsSuccess({
          ID,
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Tag deletada',
        }),
      );
    }
  } catch (err) {
    yield put(DialogsActions.setClose());
    yield put(AttendanceActions.deleteTagsFailure());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível deletar a tag',
      }),
    );
  }
}

// User Tags
function* getUserTags({
  payload: { Room },
}: ReturnType<typeof AttendanceActions.getUserTagsRequest>): any {
  try {
    const response = yield call(userTagsApi.get, { Room });

    if (response.status === 200) {
      yield put(
        AttendanceActions.getUserTagsSuccess({
          Tags: response.data,
        }),
      );
    }
  } catch (err) {
    yield put(AttendanceActions.getUserTagsFailure());
  }
}

function* createUserTags({
  payload: { Room, Tag },
}: ReturnType<typeof AttendanceActions.createUserTagsRequest>): any {
  try {
    const response = yield call(userTagsApi.create, {
      data: { room: Room, tag: Tag },
    });

    const tags = yield select(
      (state: ApplicationState) => state.attendance.Tags,
    );

    const newTag = tags.filter((tag: TagsInterface) => tag.tag === Tag);

    if (response.status === 200) {
      const { ID } = response.data;

      yield put(
        AttendanceActions.createUserTagsSuccess({
          Room,
          Tag,
          ID,
        }),
      );

      if (!newTag.length) {
        yield put(
          AttendanceActions.createTagsSuccess({
            Tag: {
              ID,
              tag: Tag,
              userRegister: [],
              messageRegister: [],
            },
          }),
        );
      }

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Tag criada',
        }),
      );
    }
  } catch (err) {
    yield put(AttendanceActions.createUserTagsFailure());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível criar a tag',
      }),
    );
  }
}

function* deleteUserTags({
  payload: { Room, ID },
}: ReturnType<typeof AttendanceActions.deleteUserTagsRequest>): any {
  try {
    const response = yield call(userTagsApi.delete, {
      ID,
      room: Room,
    });

    if (response.status === 200) {
      yield put(
        AttendanceActions.deleteUserTagsSuccess({
          Room,
          ID,
        }),
      );
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Tag deletada',
        }),
      );
    }
  } catch (err) {
    yield put(AttendanceActions.deleteUserTagsFailure());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível deletar a tag',
      }),
    );
  }
}

function* createMessageTag({
  payload: { messageID, Room, Tag },
}: ReturnType<typeof AttendanceActions.createMessageTagRequest>): any {
  try {
    const response = yield call(messageTagsApi.create, {
      data: { id: messageID, room: Room, tag: Tag },
    });

    const tags = yield select(
      (state: ApplicationState) => state.attendance.Tags,
    );

    const newTag = tags.filter((tag: TagsInterface) => tag.tag === Tag);

    if (response.status === 200) {
      const { ID } = response.data;

      yield put(
        AttendanceActions.createMessageTagSuccess({
          messageID,
          Room,
          Tag,
          ID,
        }),
      );

      if (!newTag.length) {
        yield put(
          AttendanceActions.createTagsSuccess({
            Tag: {
              ID,
              tag: Tag,
              userRegister: [],
              messageRegister: [],
            },
          }),
        );
      }
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Tag criada',
        }),
      );
    }
  } catch (err) {
    yield put(AttendanceActions.createMessageTagFailure());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível criar a tag',
      }),
    );
  }
}

function* deleteMessageTag({
  payload: { messageID, Room, ID },
}: ReturnType<typeof AttendanceActions.deleteMessageTagRequest>): any {
  try {
    const response = yield call(messageTagsApi.delete, {
      ID,
      Room,
      messageID,
    });

    if (response.status === 200) {
      yield put(
        AttendanceActions.deleteMessageTagSuccess({
          messageID,
          Room,
          ID,
        }),
      );
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Tag deletada',
        }),
      );
    }
  } catch (err) {
    yield put(AttendanceActions.deleteMessageTagFailure());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível deletar a tag',
      }),
    );
  }
}

// Qualification
function* getQualification({
  payload: { ID },
}: ReturnType<typeof AttendanceActions.getQualificationRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando...' }));
    const response = yield call(qualificationApi.get, { ID });

    if (response.status === 200) {
      yield put(
        AttendanceActions.getQualificationSuccess({
          Data: response.data.qualifications,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (err) {
    yield put(DialogsActions.setClose());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível listar a qualificação',
      }),
    );
    yield put(AttendanceActions.getQualificationFailure());
  }
}

function* createQualification({
  payload: { ID, key, value },
}: ReturnType<typeof AttendanceActions.createQualificationRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Criando qualificação...' }),
    );
    const qualifications: { [key: string]: any } = {};
    qualifications[key] = value;

    const response = yield call(qualificationApi.create, {
      data: { ID, qualifications },
    });

    if (response.status === 200) {
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Qualificação criada com sucesso',
        }),
      );

      const qualificationResponse = yield call(qualificationApi.get, { ID });
      if (qualificationResponse.status === 200) {
        yield put(
          AttendanceActions.getQualificationSuccess({
            Data: response.data.qualifications,
          }),
        );
      }

      yield put(DialogsActions.setClose());
    }
  } catch (err) {
    yield put(DialogsActions.setClose());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível criar a qualificação',
      }),
    );
    yield put(AttendanceActions.createQualificationFailure());
  }
}

// Automatic Replies
function* listReplies({
  payload,
}: ReturnType<typeof AttendanceActions.listRepliesRequest>): any {
  try {
    const { body, filtering } = payload;
    if (!filtering) {
      yield put(DialogsActions.setOpenLoading({ title: 'Carregando' }));
    }
    const response = yield call(replyApi.list, { params: { ...body } });

    if (response.status === 200) {
      yield put(
        AttendanceActions.listRepliesSuccess({
          Replies: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (err) {
    yield put(DialogsActions.setClose());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível carregar as respostas automáticas',
      }),
    );
    yield put(AttendanceActions.listRepliesFailure());
  }
}

function* filterReplies({
  payload,
}: ReturnType<typeof AttendanceActions.filterRepliesRequest>): any {
  try {
    const response = yield call(replyApi.list, { params: { ...payload } });

    if (response.status === 200) {
      yield put(
        AttendanceActions.filterRepliesSuccess({
          Replies: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (err) {
    yield put(DialogsActions.setClose());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível carregar as respostas automáticas',
      }),
    );
    yield put(AttendanceActions.filterRepliesFailure());
  }
}

function* getReply({
  payload: { ID },
}: ReturnType<typeof AttendanceActions.getReplyRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando' }));
    const response = yield call(replyApi.get, { ID });

    if (response.status === 200) {
      yield put(AttendanceActions.getReplySuccess({ Reply: response.data }));
      yield put(DialogsActions.setClose());
    }
  } catch (err) {
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível editar a resposta',
      }),
    );
    yield put(AttendanceActions.getReplyFailure());
  }
}

function* createReply({
  payload: { Title, Description },
}: ReturnType<typeof AttendanceActions.createReplyRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Criando Resposta automática' }),
    );

    const response = yield call(replyApi.create, {
      data: { title: Title, description: Description },
    });

    if (response.status === 200) {
      const { ID } = response.data;

      yield put(
        AttendanceActions.createReplySuccess({
          Reply: { ID, title: Title, description: Description },
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Resposta criada com sucesso',
        }),
      );
      yield put(push(PATH.ATTENDANCE.CONFIGURATIONS));
    }
  } catch (err) {
    yield put(DialogsActions.setClose());
    yield put(AttendanceActions.createReplyFailure());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível criar a resposta',
      }),
    );
  }
}

function* updateReply({
  payload: { Reply },
}: ReturnType<typeof AttendanceActions.updateReplyRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({
        title: 'Atualizando Resposta automática',
      }),
    );

    const { ID, title, description, createdAt } = Reply;

    const response = yield call(replyApi.update, {
      ID,
      data: { title, description, createdAt },
    });

    if (response.status === 200) {
      yield put(
        AttendanceActions.updateReplySuccess({
          Reply,
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Resposta atualizada com sucesso',
        }),
      );
    }
  } catch (err) {
    yield put(DialogsActions.setClose());
    yield put(AttendanceActions.updateReplyFailure());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível atualizar a resposta',
      }),
    );
  }
}

function* deleteReply({
  payload: { ID },
}: ReturnType<typeof AttendanceActions.deleteReplyRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Deletando Resposta automática' }),
    );
    const response = yield call(replyApi.delete, { ID });

    if (response.status === 200) {
      yield put(
        AttendanceActions.deleteReplySuccess({
          ID,
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Resposta automática deletada',
        }),
      );
    }
  } catch (err) {
    yield put(DialogsActions.setClose());
    yield put(AttendanceActions.deleteReplyFailure());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível deletar a resposta automática',
      }),
    );
  }
}

// Files
function* getFile({
  payload: { fileName },
}: ReturnType<typeof AttendanceActions.getFileRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando' }));
    const response = yield call(fileApi.get, { fileName });

    if (response.status === 200) {
      window.open(response.data.URL);
      yield put(DialogsActions.setClose());
    }
  } catch (err) {
    yield put(DialogsActions.setClose());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível carregar o arquivo',
      }),
    );
  }
}

// Current user Picture
function* getCurrentUserPicture({
  payload: { Room },
}: ReturnType<typeof AttendanceActions.getCurrentUserPictureRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando' }));

    const response = yield call(fileApi.get, { fileName: Room });

    if (response.status === 200) {
      yield put(
        AttendanceActions.getCurrentUserPictureSuccess({
          URL: response.data.URL,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (err) {
    yield put(DialogsActions.setClose());
    yield put(AttendanceActions.getCurrentUserPictureFailure());
  }
}

// User Picture
function* getUserPicture({
  payload: { Room },
}: ReturnType<typeof AttendanceActions.getUserPictureRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando' }));
    const response = yield call(fileApi.get, { fileName: Room });

    if (response.status === 200) {
      yield put(
        AttendanceActions.getUserPictureSuccess({
          Room,
          URL: response.data.URL,
        }),
      );
      yield put(DialogsActions.setClose());
    } else {
      yield put(AttendanceActions.getUserPictureSuccess({ Room, URL: '' }));
      yield put(DialogsActions.setClose());
    }
  } catch (err) {
    yield put(DialogsActions.setClose());
    yield put(AttendanceActions.getUserPictureFailure());
  }
}

function* createFile({
  payload: { fileName, File },
}: ReturnType<typeof AttendanceActions.createFileRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando' }));
    const response = yield call(fileApi.create, {
      fileName,
      data: { file: File },
    });

    if (response.status === 200) {
      const Room = yield select(
        (state: ApplicationState) => state.attendance.Room,
      );

      const CompanyID = getCompany();

      const username = getSenderDisplayName();

      const data = {
        message: fileName,
        action: CONST_ATTENDANCE.ACTIONS.ATTENDANT_PUBLISH,
        Room,
        type: CONST_ATTENDANCE.USER_TYPES.ATTENDANT,
        messageType: CONST_ATTENDANCE.MESSAGE_TYPE.FILE,
        CompanyID,
        username,
        attendantID: getId(),
        userType: CONST_ATTENDANCE.USER_TYPES.ATTENDANT,
      };

      socket.send(JSON.stringify(data));

      // Local placeholder until the WS echo arrives; ids are strings in v2
      const id = `local-${new Date().getTime()}`;

      const attendantMessage = {
        Room,
        messageData: {
          id,
          message: fileName,
          messageType: CONST_ATTENDANCE.MESSAGE_TYPE.FILE,
          type: CONST_ATTENDANCE.USER_TYPES.ATTENDANT,
          tags: [],
          username,
          date: JSON.stringify(new Date()),
        },
      };

      yield put(
        AttendanceActions.loadNewMessageSuccess({
          message: attendantMessage,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (err) {
    yield put(DialogsActions.setClose());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível fazer upload do arquivo',
      }),
    );
  }
}

// Attendant Picture
function* getAttendantPicture({
  payload: { Room },
}: ReturnType<typeof AttendanceActions.getAttendantPictureRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando' }));
    const response = yield call(fileApi.get, { fileName: Room });

    if (response.status === 200) {
      yield put(
        AttendanceActions.getAttendantPictureSuccess({
          Room,
          URL: response.data.URL,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (err) {
    yield put(DialogsActions.setClose());
    yield put(AttendanceActions.getAttendantPictureFailure());
  }
}

function* createAttedantPicture({
  payload: { fileName, File },
}: ReturnType<typeof AttendanceActions.createAttendantPictureRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando' }));
    const response = yield call(fileApi.create, {
      fileName,
      data: { file: File },
    });

    if (response.status === 200) {
      yield put(DialogsActions.setClose());
      yield put(push(PATH.ATTENDANCE.CONFIGURATIONS));
    }
  } catch (err) {
    yield put(DialogsActions.setClose());
    yield put(push(PATH.ATTENDANCE.CONFIGURATIONS));
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível fazer o upload da foto do atendente',
      }),
    );
  }
}

// Office Hours
function* listOfficeHours({
  payload,
}: ReturnType<typeof AttendanceActions.listOfficeHoursRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando' }));
    const response = yield call(officeHoursApi.list, {
      params: { ...payload },
    });

    if (response.status === 200) {
      yield put(
        AttendanceActions.listOfficeHoursSuccess({
          OfficeHours: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (err) {
    yield put(DialogsActions.setClose());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível carregar as horarios de atendimento',
      }),
    );
    yield put(AttendanceActions.listOfficeHoursFailure());
  }
}

function* getOfficeHour({
  payload: { ID },
}: ReturnType<typeof AttendanceActions.getOfficeHoursRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando' }));
    const response = yield call(officeHoursApi.get, { ID });

    if (response.status === 200) {
      yield put(
        AttendanceActions.getOfficeHoursSuccess({ OfficeHour: response.data }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (err) {
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível editar o horário',
      }),
    );
    yield put(AttendanceActions.getOfficeHoursFailure());
  }
}

function* createOfficeHour({
  payload: { OfficeHour },
}: ReturnType<typeof AttendanceActions.createOfficeHoursRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({
        title: 'Criando horário de atendimento',
      }),
    );

    const response = yield call(officeHoursApi.create, {
      data: OfficeHour,
    });

    if (response.status === 200) {
      yield put(
        AttendanceActions.createOfficeHoursSuccess({
          OfficeHour: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Horário criado com sucesso',
        }),
      );
      yield put(push(PATH.ATTENDANCE.CONFIGURATIONS));
    }
  } catch (err) {
    yield put(DialogsActions.setClose());
    yield put(AttendanceActions.createOfficeHoursFailure());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível criar o horário',
      }),
    );
  }
}

function* updateOfficeHour({
  payload: { OfficeHour },
}: ReturnType<typeof AttendanceActions.updateOfficeHoursRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({
        title: 'Atualizando Horário de atendimento',
      }),
    );

    const { ID, period, time, message, createdAt } = OfficeHour;

    const response = yield call(officeHoursApi.update, {
      ID,
      data: { period, time, message, createdAt },
    });

    if (response.status === 200) {
      yield put(
        AttendanceActions.updateOfficeHoursSuccess({
          OfficeHour: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Horário de atendimento atualizado com sucesso',
        }),
      );
    }
  } catch (err) {
    yield put(DialogsActions.setClose());
    yield put(AttendanceActions.updateOfficeHoursFailure());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível atualizar o horário de atendimento',
      }),
    );
  }
}

function* deleteOfficeHours({
  payload: { ID },
}: ReturnType<typeof AttendanceActions.deleteOfficeHoursRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({
        title: 'Deletando horário de atendimento',
      }),
    );
    const response = yield call(officeHoursApi.delete, { ID });

    if (response.status === 200) {
      yield put(
        AttendanceActions.deleteOfficeHoursSuccess({
          ID,
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Horário de atendimento deletado',
        }),
      );
    }
  } catch (err) {
    yield put(AttendanceActions.deleteOfficeHoursFailure());
    yield put(DialogsActions.setClose());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível deletar o horário de atendimento',
      }),
    );
  }
}

// Attendants
function* listAttendants({
  payload,
}: ReturnType<typeof AttendanceActions.listAttendantsRequest>): any {
  try {
    const { body, filtering } = payload;
    if (!filtering) {
      yield put(DialogsActions.setOpenLoading({ title: 'Carregando' }));
    }
    const response = yield call(attendantsApi.list, { params: { ...body } });

    if (response.status === 200) {
      yield put(
        AttendanceActions.listAttendantsSuccess({
          Attendants: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível listar os atendentes',
      }),
    );
    yield put(AttendanceActions.listAttendantsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* getAttendant({
  payload: { ID },
}: ReturnType<typeof AttendanceActions.getAttendantRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando' }));
    const response = yield call(attendantsApi.get, { ID });

    if (response.status === 200) {
      yield put(
        AttendanceActions.getAttendantSuccess({ Attendant: response.data }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (err) {
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível editar o atendente',
      }),
    );
    yield put(AttendanceActions.getAttendantFailure());
  }
}

function* filterAttendants({
  payload,
}: ReturnType<typeof AttendanceActions.filterAttendantsRequest>): any {
  try {
    const response = yield call(attendantsApi.list, { params: { ...payload } });

    if (response.status === 200) {
      yield put(
        AttendanceActions.filterAttendantsSuccess({
          Attendants: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível listar os atendentes',
      }),
    );
    yield put(AttendanceActions.filterAttendantsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* createAttendant({
  payload: { Name, Email, Modules },
}: ReturnType<typeof AttendanceActions.createAttendantRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Criando atendente' }));

    const response = yield call(attendantsApi.create, {
      data: { name: Name, email: Email, modules: Modules },
    });

    if (response.status === 200) {
      const { ID, email } = response.data;

      yield call(() =>
        sendPasswordResetEmail(auth, email, {
          url: ENVS.PASSWORD_REDIRECT || 'https://bilhetagemdigital.com.br',
        }),
      );

      yield put(
        AttendanceActions.createAttendantSuccess({
          Attendant: {
            ID,
            name: Name,
            email: Email,
            modules: Modules,
          },
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Atendente criado com sucesso',
        }),
      );
      yield put(push(PATH.ATTENDANCE.CONFIGURATIONS));
    }
  } catch (err) {
    console.log(err);
    yield put(DialogsActions.setClose());
    yield put(AttendanceActions.createAttendantFailure());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível criar o Atendente',
      }),
    );
  }
}

function* updateAttendant({
  payload: { Attendant },
}: ReturnType<typeof AttendanceActions.updateAttendantRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Atualizando atendente' }),
    );

    const { ID, name, email, modules } = Attendant;

    const response = yield call(attendantsApi.update, {
      ID,
      data: { name, email, modules },
    });

    if (response.status === 200) {
      yield put(
        AttendanceActions.updateAttendantSuccess({
          Attendant,
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Atendente atualizado com sucesso',
        }),
      );
    }
  } catch (err) {
    yield put(DialogsActions.setClose());
    yield put(AttendanceActions.updateAttendantFailure());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível atualizar o atendente',
      }),
    );
  }
}

function* deleteAttendant({
  payload: { ID },
}: ReturnType<typeof AttendanceActions.deleteAttendantRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Excluindo atendente' }));
    const response = yield call(attendantsApi.delete, { ID });

    if (response.status === 200) {
      yield put(
        AttendanceActions.deleteAttendantSuccess({
          ID,
        }),
      );
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Atendente deletado',
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (err) {
    yield put(DialogsActions.setClose());
    yield put(AttendanceActions.deleteAttendantFailure());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível deletar o atendente',
      }),
    );
  }
}

function* getCurrentAttendant({
  payload: { ID },
}: ReturnType<typeof AttendanceActions.getCurrentAttendantRequest>): any {
  try {
    const response = yield call(attendantsApi.get, { ID });

    if (response.status === 200) {
      yield put(
        AttendanceActions.getCurrentAttendantSuccess({
          Attendant: response.data,
        }),
      );

      const { name } = response.data;

      setAttendantUsername(name);
    }
  } catch (err) {
    yield put(AttendanceActions.getCurrentAttendantFailure());
  }
}

function* getToken(): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando' }));
    const response = yield call(tokenApi.get);

    if (response.status === 200) {
      yield put(
        AttendanceActions.getTokenSuccess({ Token: response.data.apiKey }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (err) {
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível exibir o token',
      }),
    );
    yield put(AttendanceActions.getTokenFailure());
  }
}

function* createToken(): any {
  const Token = yield select(
    (state: ApplicationState) => state.attendance.Token,
  );

  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Criando Token' }));

    const response = yield call(tokenApi.create);

    if (response.status === 200) {
      yield put(
        AttendanceActions.createTokenSuccess({
          Token: response.data.apiKey,
        }),
      );
      yield put(DialogsActions.setClose());

      if (Token) {
        yield put(
          SnackbarsActions.setOpen({
            open: true,
            type: 'success',
            message: 'Token atualizado com sucesso',
          }),
        );
      } else {
        yield put(
          SnackbarsActions.setOpen({
            open: true,
            type: 'success',
            message: 'Token criado com sucesso',
          }),
        );
      }
    }
  } catch (err) {
    yield put(DialogsActions.setClose());
    yield put(AttendanceActions.createTokenFailure());

    if (Token) {
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'error',
          message: 'Não foi possível atualizar o token',
        }),
      );
    } else {
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'error',
          message: 'Não foi possível criar o token',
        }),
      );
    }
  }
}

function* deleteToken(): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Excluindo token' }));
    const response = yield call(tokenApi.delete);

    if (response.status === 200) {
      yield put(AttendanceActions.deleteTokenSuccess());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Token deletado',
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (err) {
    yield put(DialogsActions.setClose());
    yield put(AttendanceActions.deleteTokenFailure());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível deletar o token',
      }),
    );
  }
}

// WordCount Chart
function* getWordCount({
  payload: { startDate, endDate },
}: ReturnType<typeof AttendanceActions.getWordCountRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando' }));
    const response = yield call(wordCountChartApi.get, { startDate, endDate });

    if (response.status === 200) {
      yield put(
        AttendanceActions.getWordCountSuccess({
          wordCountData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (err) {
    yield put(DialogsActions.setClose());
    yield put(AttendanceActions.getWordCountFailure());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível carregar a nuvem de palavras',
      }),
    );
  }
}

// HeatMap Chart
function* getHeatMap({
  payload: { startDate, endDate },
}: ReturnType<typeof AttendanceActions.getHeatMapRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando' }));
    const response = yield call(heatMapChartApi.get, { startDate, endDate });

    if (response.status === 200) {
      yield put(
        AttendanceActions.getHeatMapSuccess({
          heatmapData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (err) {
    yield put(DialogsActions.setClose());
    yield put(AttendanceActions.getHeatMapFailure());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível carregar o Mapa de calor',
      }),
    );
  }
}

// Office Hours
function* listWebhook({
  payload,
}: ReturnType<typeof AttendanceActions.listWebhookRequest>): any {
  try {
    const { body, filtering } = payload;
    if (!filtering) {
      yield put(DialogsActions.setOpenLoading({ title: 'Carregando' }));
    }
    const response = yield call(webhookApi.list, {
      params: { ...body },
    });

    if (response.status === 200) {
      yield put(
        AttendanceActions.listWebhookSuccess({
          webhookList: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (err) {
    yield put(DialogsActions.setClose());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível carregar os webhooks',
      }),
    );
    yield put(AttendanceActions.listWebhookFailure());
  }
}

function* filterWebhook({
  payload,
}: ReturnType<typeof AttendanceActions.filterWebhookRequest>): any {
  try {
    const response = yield call(webhookApi.list, {
      params: { ...payload },
    });

    if (response.status === 200) {
      yield put(
        AttendanceActions.filterWebhookSuccess({
          webhookList: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (err) {
    yield put(DialogsActions.setClose());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível carregar os webhooks',
      }),
    );
    yield put(AttendanceActions.filterWebhookFailure());
  }
}

function* createWebhook({
  payload: { type, url },
}: ReturnType<typeof AttendanceActions.createWebhookRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({
        title: 'Criando webhook',
      }),
    );

    const response = yield call(webhookApi.create, {
      data: { type, url },
    });

    if (response.status === 200) {
      const { ID, createdAt } = response.data;

      yield put(
        AttendanceActions.createWebhookSuccess({
          webhookData: {
            ID,
            type,
            url,
            createdAt,
          },
        }),
      );
      yield put(push(PATH.ATTENDANCE.CONFIGURATIONS));
    }
  } catch (err) {
    yield put(DialogsActions.setClose());
    yield put(AttendanceActions.createOfficeHoursFailure());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível criar o webhook',
      }),
    );
  }
}

function* updateWebhook({
  payload: { webhookData },
}: ReturnType<typeof AttendanceActions.updateWebhookRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({
        title: 'Atualizando o webhook',
      }),
    );

    const { ID, type, url, createdAt } = webhookData;

    const response = yield call(webhookApi.update, {
      ID,
      data: { type, url, createdAt },
    });

    if (response.status === 200) {
      yield put(
        AttendanceActions.updateWebhookSuccess({
          webhookData,
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          message: 'Webhook atualizado com sucesso',
        }),
      );
    }
  } catch (err) {
    yield put(DialogsActions.setClose());
    yield put(AttendanceActions.updateOfficeHoursFailure());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível atualizar o webhook',
      }),
    );
  }
}

function* deleteWebhook({
  payload: { ID },
}: ReturnType<typeof AttendanceActions.deleteWebhookRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({
        title: 'Deletando o webhook',
      }),
    );
    const response = yield call(webhookApi.delete, { ID });

    if (response.status === 200) {
      yield put(
        AttendanceActions.deleteWebhookSuccess({
          ID,
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Webhook deletado',
        }),
      );
    }
  } catch (err) {
    yield put(AttendanceActions.deleteWebhookFailure());
    yield put(DialogsActions.setClose());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível deletar o webhook',
      }),
    );
  }
}

// Tags Lines Chart Data
function* getTagsLinesChart({
  payload: { startDate, endDate, type },
}: ReturnType<typeof AttendanceActions.getTagsLinesChartRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando' }));
    const response = yield call(tagsLinesChartApi.get, {
      startDate,
      endDate,
      type,
    });

    const Tags = yield select(
      (state: ApplicationState) => state.attendance.Tags,
    );

    const TagsLabel = Tags.map((tag: any) => tag.tag);

    TagsLabel.unshift('date');

    response.data.unshift(TagsLabel);

    if (response.status === 200) {
      yield put(
        AttendanceActions.getTagsLinesChartSuccess({
          TagsLinesChartData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (err) {
    yield put(DialogsActions.setClose());
    yield put(AttendanceActions.getTagsLinesChartFailure());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível carregar o gráfico de tags',
      }),
    );
  }
}

// Rating Chart Data
function* getRatingChart({
  payload: { startDate, endDate, attendantsId },
}: ReturnType<typeof AttendanceActions.getRatingChartRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando' }));
    const response = yield call(ratingApi.listChart, {
      startDate,
      endDate,
      data: {
        attendantsId,
      },
    });

    if (response.status === 200) {
      yield put(
        AttendanceActions.getRatingChartSuccess({
          RatingChartData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (err) {
    yield put(DialogsActions.setClose());
    yield put(AttendanceActions.getRatingChartFailure());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message:
          'Não foi possível carregar o gráfico de avaliação de atendentes',
      }),
    );
  }
}

// Close Conversation Chart Data
function* getCloseConversationChart({
  payload: { startDate, endDate, attendantsId },
}: ReturnType<typeof AttendanceActions.getCloseConversationChartRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando' }));
    const response = yield call(closeConversationChartApi.list, {
      startDate,
      endDate,
      data: {
        attendantsId,
      },
    });

    if (response.status === 200) {
      yield put(
        AttendanceActions.getCloseConversationChartSuccess({
          CloseConversationChartData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (err) {
    yield put(DialogsActions.setClose());
    yield put(AttendanceActions.getCloseConversationChartFailure());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message:
          'Não foi possível carregar o gráfico de tempo médio dos atendimentos',
      }),
    );
  }
}

// First Reply Chart Data
function* getFirstReplyChart({
  payload: { startDate, endDate, attendantsId },
}: ReturnType<typeof AttendanceActions.getFirstReplyChartRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando' }));
    const response = yield call(firstReplyChartApi.list, {
      startDate,
      endDate,
      data: {
        attendantsId,
      },
    });

    if (response.status === 200) {
      yield put(
        AttendanceActions.getFirstReplyChartSuccess({
          FirstReplyChartData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (err) {
    yield put(DialogsActions.setClose());
    yield put(AttendanceActions.getFirstReplyChartFailure());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message:
          'Não foi possível carregar o gráfico de tempo médio de primeira resposta',
      }),
    );
  }
}

// Conversation Reply Chart Data
function* getConversationReplyChart({
  payload: { startDate, endDate },
}: ReturnType<typeof AttendanceActions.getConversationReplyChartRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando' }));
    const response = yield call(conversationReplyChartApi.list, {
      startDate,
      endDate,
    });

    if (response.status === 200) {
      yield put(
        AttendanceActions.getConversationReplyChartSuccess({
          ConversationReplyChartData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (err) {
    yield put(DialogsActions.setClose());
    yield put(AttendanceActions.getConversationReplyChartFailure());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message:
          'Não foi possível carregar o gráfico de conversas com respostas',
      }),
    );
  }
}

// Human and Chatbot Chart Data
function* getHumanAndChatbotChartData({
  payload: { startDate, endDate },
}: ReturnType<typeof AttendanceActions.getHumanAndChatbotChartRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando...' }));
    const response = yield call(messagesCountApi.list, {
      startDate,
      endDate,
    });

    if (response.status === 200) {
      yield put(
        AttendanceActions.getHumanAndChatbotChartSuccess({
          humanAndChatbotChartData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (err) {
    yield put(DialogsActions.setClose());
    yield put(AttendanceActions.getHumanAndChatbotChartFailure());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message:
          'Não foi possível carregar o gráfico de atendimentos humano e chatbot',
      }),
    );
  }
}

// First Reply Chart Scatter Data
function* getFirstReplyScatterChart({
  payload: { startDate, endDate },
}: ReturnType<typeof AttendanceActions.getFirstReplyScatterChartRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando' }));
    const response = yield call(firstReplyChartApi.listScatter, {
      startDate,
      endDate,
    });

    if (response.status === 200) {
      yield put(
        AttendanceActions.getFirstReplyScatterChartSuccess({
          FirstReplyScatterChartData: response.data.timestamps,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (err) {
    yield put(DialogsActions.setClose());
    yield put(AttendanceActions.getFirstReplyScatterChartFailure());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message:
          'Não foi possível carregar o gráfico de dispersão de primeira resposta',
      }),
    );
  }
}

// Attendance History
function* getAttendanceHistory({
  payload,
}: ReturnType<typeof AttendanceActions.getAttendanceHistoryRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({
        title: 'Carregando histórico de conversas',
      }),
    );

    const response = yield call(attendancesApi.get, {
      params: { ...payload },
    });

    if (response.status === 200) {
      // v2 message items carry ID (string); mirror it into `id` for the UI
      const attendanceHistory = (response.data || []).map(
        (conversation: any) => ({
          ...conversation,
          messages: (conversation.messages || []).map(normalizeMessage),
        }),
      );

      yield put(
        AttendanceActions.getAttendanceHistorySuccess({
          attendanceHistory,
        }),
      );

      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    // eslint-disable-next-line no-console
    console.error('Attendance History - list - error', error);

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );

    yield put(DialogsActions.setClose());

    yield put(
      AttendanceActions.getAttendanceHistoryFailure({
        statusCode: error.response.status,
      }),
    );
  }
}

// Rating List
function* listRating({
  payload: {
    startDate,
    endDate,
    attendantId,
    ratingId,
    createdAt,
    scanForward,
    limit,
  },
}: ReturnType<typeof AttendanceActions.listRatingRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando' }));
    const response = yield call(ratingApi.list, {
      params: {
        startDate,
        endDate,
        attendantId: attendantId || '',
        ratingId: ratingId || '',
        createdAt: createdAt || 0,
        scanForward,
        limit: limit || 10,
      },
    });

    if (response.status === 200) {
      yield put(
        AttendanceActions.listRatingSuccess({
          ratingList: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (err) {
    yield put(DialogsActions.setClose());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível exibir as avaliações',
      }),
    );
    yield put(AttendanceActions.listTagsFailure());
  }
}

// Rating Number of Messages
function* listNumberOfMessages({
  payload: { startDate, endDate },
}: ReturnType<typeof AttendanceActions.listNumberOfMessagesRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando' }));
    const response = yield call(numberOfMessagesApi.list, {
      params: {
        startDate,
        endDate,
      },
    });

    if (response.status === 200) {
      yield put(
        AttendanceActions.listNumberOfMessagesSuccess({
          numberOfMessagesList: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (err) {
    yield put(DialogsActions.setClose());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível exibir o número de mensagens',
      }),
    );
    yield put(AttendanceActions.listNumberOfMessagesFailure());
  }
}

export const attendanceSagas = [
  takeLatest(AttendanceTypes.INIT_WEBSOCKET_CHANNEL, handleWebsocketConnection),
  takeLatest(AttendanceTypes.LIST_OPENED_USERS_REQUEST, listOpenedUsers),
  takeLatest(AttendanceTypes.LIST_CLOSED_USERS_REQUEST, listClosedUsers),
  takeLatest(AttendanceTypes.LIST_CHATBOT_USERS_REQUEST, listChatbotUsers),
  takeLatest(AttendanceTypes.FILTER_USERS_BY_NAME_REQUEST, filterUsersByName),
  takeLatest(
    AttendanceTypes.FILTER_USERS_BY_MESSAGE_REQUEST,
    filterUsersByMessage,
  ),
  takeLatest(AttendanceTypes.FILTER_USERS_BY_TAG_REQUEST, filterUsersByTag),
  takeLatest(AttendanceTypes.FILTER_TAG_BY_NAME_REQUEST, filterTagByName),
  takeLatest(AttendanceTypes.GET_MESSAGES_REQUEST, getMessages),
  // takeLeading: ignore load-older requests while one is already in flight
  takeLeading(AttendanceTypes.GET_OLDER_MESSAGES_REQUEST, getOlderMessages),
  takeLatest(AttendanceTypes.UPDATE_USER_REQUEST, updateUser),
  takeLatest(AttendanceTypes.GET_TAG_REQUEST, getTag),
  takeLatest(AttendanceTypes.LIST_TAGS_REQUEST, listTags),
  takeLatest(AttendanceTypes.FILTER_TAGS_REQUEST, filterTags),
  takeLatest(AttendanceTypes.CREATE_TAGS_REQUEST, createTag),
  takeLatest(AttendanceTypes.UPDATE_TAGS_REQUEST, updateTag),
  takeLatest(AttendanceTypes.DELETE_TAGS_REQUEST, deleteTag),
  takeLatest(AttendanceTypes.GET_USER_TAGS_REQUEST, getUserTags),
  takeLatest(AttendanceTypes.CREATE_USER_TAGS_REQUEST, createUserTags),
  takeLatest(AttendanceTypes.DELETE_USER_TAGS_REQUEST, deleteUserTags),
  takeLatest(AttendanceTypes.CREATE_MESSAGE_TAG_REQUEST, createMessageTag),
  takeLatest(AttendanceTypes.DELETE_MESSAGE_TAG_REQUEST, deleteMessageTag),
  takeLatest(AttendanceTypes.GET_QUALIFICATION_REQUEST, getQualification),
  takeLatest(AttendanceTypes.CREATE_QUALIFICATION_REQUEST, createQualification),
  takeLatest(AttendanceTypes.LIST_REPLY_REQUEST, listReplies),
  takeLatest(AttendanceTypes.FILTER_REPLY_REQUEST, filterReplies),
  takeLatest(AttendanceTypes.GET_REPLY_REQUEST, getReply),
  takeLatest(AttendanceTypes.CREATE_REPLY_REQUEST, createReply),
  takeLatest(AttendanceTypes.UPDATE_REPLY_REQUEST, updateReply),
  takeLatest(AttendanceTypes.DELETE_REPLY_REQUEST, deleteReply),
  takeEvery(AttendanceTypes.GET_FILE_REQUEST, getFile),
  takeLatest(AttendanceTypes.CREATE_FILE_REQUEST, createFile),
  takeLatest(
    AttendanceTypes.GET_CURRENT_USER_PICTURE_REQUEST,
    getCurrentUserPicture,
  ),
  takeEvery(AttendanceTypes.GET_USER_PICTURE_REQUEST, getUserPicture),
  takeEvery(AttendanceTypes.GET_ATTENDANT_PICTURE_REQUEST, getAttendantPicture),
  takeLatest(
    AttendanceTypes.CREATE_ATTENDANT_PICTURE_REQUEST,
    createAttedantPicture,
  ),
  takeLatest(AttendanceTypes.LIST_OFFICE_HOURS_REQUEST, listOfficeHours),
  takeLatest(AttendanceTypes.GET_OFFICE_HOURS_REQUEST, getOfficeHour),
  takeLatest(AttendanceTypes.CREATE_OFFICE_HOURS_REQUEST, createOfficeHour),
  takeLatest(AttendanceTypes.UPDATE_OFFICE_HOURS_REQUEST, updateOfficeHour),
  takeLatest(AttendanceTypes.DELETE_OFFICE_HOURS_REQUEST, deleteOfficeHours),
  takeLatest(AttendanceTypes.LIST_ATTENDANTS_REQUEST, listAttendants),
  takeLatest(AttendanceTypes.FILTER_ATTENDANTS_REQUEST, filterAttendants),
  takeLatest(AttendanceTypes.GET_ATTENDANTS_REQUEST, getAttendant),
  takeLatest(AttendanceTypes.CREATE_ATTENDANTS_REQUEST, createAttendant),
  takeLatest(AttendanceTypes.UPDATE_ATTENDANTS_REQUEST, updateAttendant),
  takeLatest(AttendanceTypes.DELETE_ATTENDANTS_REQUEST, deleteAttendant),
  takeEvery(AttendanceTypes.GET_CURRENT_ATTENDANT_REQUEST, getCurrentAttendant),
  takeLatest(AttendanceTypes.GET_TOKEN_REQUEST, getToken),
  takeLatest(AttendanceTypes.CREATE_TOKEN_REQUEST, createToken),
  takeLatest(AttendanceTypes.DELETE_TOKEN_REQUEST, deleteToken),
  takeLatest(AttendanceTypes.GET_HEATMAP_REQUEST, getHeatMap),
  takeLatest(AttendanceTypes.GET_WORDCOUNT_REQUEST, getWordCount),
  takeLatest(AttendanceTypes.GET_TAGS_LINES_CHART_REQUEST, getTagsLinesChart),
  takeLatest(AttendanceTypes.GET_RATING_CHART_REQUEST, getRatingChart),
  takeLatest(
    AttendanceTypes.GET_CLOSE_CONVERSATION_CHART_REQUEST,
    getCloseConversationChart,
  ),
  takeLatest(AttendanceTypes.GET_FIRST_REPLY_CHART_REQUEST, getFirstReplyChart),
  takeLatest(
    AttendanceTypes.GET_FIRST_REPLY_SCATTER_CHART_REQUEST,
    getFirstReplyScatterChart,
  ),
  takeLatest(
    AttendanceTypes.GET_CONVERSATION_REPLY_CHART_REQUEST,
    getConversationReplyChart,
  ),
  takeLatest(
    AttendanceTypes.GET_HUMAN_AND_CHATBOT_MESSAGES_CHART_REQUEST,
    getHumanAndChatbotChartData,
  ),
  takeLatest(AttendanceTypes.LIST_WEBHOOK_REQUEST, listWebhook),
  takeLatest(AttendanceTypes.FILTER_WEBHOOK_REQUEST, filterWebhook),
  takeLatest(AttendanceTypes.CREATE_WEBHOOK_REQUEST, createWebhook),
  takeLatest(AttendanceTypes.UPDATE_WEBHOOK_REQUEST, updateWebhook),
  takeLatest(AttendanceTypes.DELETE_WEBHOOK_REQUEST, deleteWebhook),
  takeLatest(
    AttendanceTypes.GET_ATTENDANCE_HISTORY_REQUEST,
    getAttendanceHistory,
  ),
  takeLatest(AttendanceTypes.LIST_RATING_REQUEST, listRating),
  takeLatest(
    AttendanceTypes.LIST_NUMBER_OF_MESSAGES_REQUEST,
    listNumberOfMessages,
  ),
];
